import React from "react";
import { humanEUR } from "../../cost-control/forecast/format";
import { compactNumber } from "../format";
import IdleCapacityNote from "./IdleCapacityNote";

// Period composition for whatever the current breakdown is. One consistent
// analysis model beats permanent "Top API keys" and "Top models" cards: change
// the breakdown and this table changes with it.
function Pct({ value }) {
  if (value == null) return <span className="text-text-3">—</span>;
  const rounded = Math.round(value * 100);
  const up = rounded >= 0;
  return (
    <span className={up ? "text-danger" : "text-success"}>
      {up ? "+" : "−"}
      {Math.abs(rounded)}%
    </span>
  );
}

// hasComparison gates the Change column. Go serialises ChangePct as a float64,
// so "No comparison" arrives as 0, not null — rendering it put a red "+0%" on
// every row, a measurement that was never taken presented as a finding. The
// column is dropped rather than filled with dashes: a column of dashes is
// still a column asking to be read.
export default function ContributorsTable({
  contributors, breakdownLabel, ownership, idleCapacity, hasComparison,
}) {
  const rows = contributors || [];
  const showChange = Boolean(hasComparison);

  return (
    <div className="rounded-card border border-card-bd bg-card p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[15px] font-semibold text-text">Top contributors</div>
        {ownership && (
          <div className="text-[12px] text-text-3">
            Allocated by the same rule that assigns cost, not metered per owner
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="py-12 text-center text-[13px] text-text-3">No consumption to break down.</div>
      ) : (
        <table style={{ width: "100%" }} className="mt-3 text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
              <th className="pb-2">{breakdownLabel}</th>
              <th className="pb-2 text-right">Tokens</th>
              <th className="pb-2 text-right">Share</th>
              <th className="pb-2 text-right">Events</th>
              <th className="pb-2 text-right">API-equivalent</th>
              {showChange && <th className="pb-2 text-right">Change</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.group} className="border-t border-card-bd">
                <td className="py-2 text-text">{r.group}</td>
                <td className="py-2 text-right tabular-nums text-text">{compactNumber(r.tokens)}</td>
                <td className="py-2 text-right tabular-nums text-text-2">{Math.round((r.share || 0) * 100)}%</td>
                <td className="py-2 text-right tabular-nums text-text-2">{compactNumber(r.events || 0)}</td>
                <td className="py-2 text-right tabular-nums text-text">{humanEUR(r.apiEquivalent || 0)}</td>
                {showChange && (
                  <td className="py-2 text-right tabular-nums">
                    <Pct value={r.changePct} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <IdleCapacityNote idleCapacity={idleCapacity} className="mt-3">
        {`It belongs to no ${String(breakdownLabel).toLowerCase()}, so these rows will not sum to the actual-cost KPI.`}
      </IdleCapacityNote>
    </div>
  );
}
